/**
 * IngredientRow Component
 *
 * Single row in the ingredient form list with ingredient select,
 * quantity, unit, notes, optional toggle, and remove button.
 */

import type { Ingredient } from '@/types/food';

export interface RecipeIngredientFormData {
  id?: number;
  ingredient_id: number;
  quantity: number | null;
  unit: string;
  notes: string;
  optional: boolean;
  position: number;
  _destroy?: boolean;
}

interface IngredientRowProps {
  ingredient: RecipeIngredientFormData;
  availableIngredients: Ingredient[];
  onChange: (updated: RecipeIngredientFormData) => void;
  onRemove: () => void;
  index: number;
}

/**
 * Common unit options
 */
const UNIT_OPTIONS = ['g', 'kg', 'ml', 'l', 'pcs', 'tbsp', 'tsp', 'cup', 'oz', 'lb'];

/**
 * Drag handle icon (visual only)
 */
function DragHandleIcon() {
  return (
    <svg
      className="w-5 h-5 text-gray-400"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
      />
    </svg>
  );
}

/**
 * X icon for remove button
 */
function XIcon() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

export function IngredientRow({
  ingredient,
  availableIngredients,
  onChange,
  onRemove,
  index,
}: IngredientRowProps) {
  const handleFieldChange = (
    field: keyof RecipeIngredientFormData,
    value: string | number | boolean | null
  ) => {
    onChange({ ...ingredient, [field]: value });
  };

  return (
    <div className="grid grid-cols-[1fr_100px_80px_1fr_80px_auto] gap-3 items-start p-4 bg-gray-50 rounded-lg">
      {/* Ingredient select */}
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-gray-700">Ingredient</label>
        <select
          value={ingredient.ingredient_id || ''}
          onChange={(e) => handleFieldChange('ingredient_id', Number(e.target.value))}
          className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 bg-white"
          aria-label={`Ingredient ${index + 1}`}
        >
          <option value="">Select ingredient</option>
          {availableIngredients.map((ing) => (
            <option key={ing.id} value={ing.id}>
              {ing.name}
            </option>
          ))}
        </select>
      </div>

      {/* Quantity */}
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-gray-700">Quantity</label>
        <input
          type="number"
          value={ingredient.quantity ?? ''}
          onChange={(e) =>
            handleFieldChange(
              'quantity',
              e.target.value ? Number(e.target.value) : null
            )
          }
          placeholder="0"
          min="0"
          step="any"
          className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
          aria-label={`Quantity for ingredient ${index + 1}`}
        />
      </div>

      {/* Unit */}
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-gray-700">Unit</label>
        <select
          value={ingredient.unit || ''}
          onChange={(e) => handleFieldChange('unit', e.target.value)}
          className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 bg-white"
          aria-label={`Unit for ingredient ${index + 1}`}
        >
          <option value="">-</option>
          {UNIT_OPTIONS.map((unit) => (
            <option key={unit} value={unit}>
              {unit}
            </option>
          ))}
        </select>
      </div>

      {/* Notes */}
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-gray-700">Notes</label>
        <input
          type="text"
          value={ingredient.notes || ''}
          onChange={(e) => handleFieldChange('notes', e.target.value)}
          placeholder="e.g., finely chopped"
          className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
          aria-label={`Notes for ingredient ${index + 1}`}
        />
      </div>

      {/* Optional checkbox */}
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-gray-700">Optional</label>
        <div className="flex items-center h-[42px]">
          <input
            type="checkbox"
            checked={ingredient.optional}
            onChange={(e) => handleFieldChange('optional', e.target.checked)}
            className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            aria-label={`Mark ingredient ${index + 1} as optional`}
          />
        </div>
      </div>

      {/* Remove button and drag handle */}
      <div className="flex items-center gap-1 mt-6">
        <button
          type="button"
          onClick={onRemove}
          className="w-9 h-9 flex items-center justify-center text-red-600 hover:bg-red-50 rounded-md transition-colors"
          aria-label={`Remove ingredient ${index + 1}`}
        >
          <XIcon />
        </button>
        <div
          className="w-6 h-9 flex items-center justify-center cursor-grab"
          aria-label="Drag to reorder"
        >
          <DragHandleIcon />
        </div>
      </div>
    </div>
  );
}
